import { createContext, ReactNode, useCallback, use } from 'react'

import { usePathname } from 'next/navigation'
import { useLocalStorage } from 'react-use'
import { toast } from 'sonner'

import { TransactionTitle } from '~/components/component-library/TransactionTitle'
import { InboundTransactionDialogContent } from '~/components/smart-accounts/InboundTransactionDialog'
import { useAuth } from '~/context/AuthProvider'
import { OrgAssetTransfer } from '~/types'

type INotificationContext = {
  sendInboundToastNotification: (transfer: OrgAssetTransfer) => void
}

const TRANSFER_HISTORY_LIMIT = 100

const NotificationContext = createContext<INotificationContext | undefined>(
  undefined,
)

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const { setGlobalModalState } = useAuth()
  const pathname = usePathname()

  const [notifiedInboundTransfers, setNotifiedInboundTransfers] =
    useLocalStorage<string[]>(
      'splits.smartAccounts.notifiedInboundTransfers',
      [],
    )

  const sendInboundToastNotification = useCallback(
    async (transfer: OrgAssetTransfer) => {
      // Don't display inbound toasts in the Connect pop-up window.
      if (pathname.startsWith('/connect')) return

      if (!notifiedInboundTransfers?.includes(transfer.id)) {
        const updatedTransfers = [
          ...(notifiedInboundTransfers ?? []).slice(
            -TRANSFER_HISTORY_LIMIT + 1,
          ),
          transfer.id,
        ]

        setNotifiedInboundTransfers(updatedTransfers)

        if (transfer.titleChunks && transfer.titleChunks.length > 0) {
          toast.success(<TransactionTitle transaction={transfer} unstyled />, {
            action: {
              label: 'View details',
              onClick: () => {
                setGlobalModalState({
                  isOpen: true,
                  content: (
                    <InboundTransactionDialogContent
                      assetTransfer={transfer}
                      onClose={() => {}}
                    />
                  ),
                })
              },
            },
          })
        }
      }
    },
    [
      notifiedInboundTransfers,
      pathname,
      setNotifiedInboundTransfers,
      setGlobalModalState,
    ],
  )

  return (
    <NotificationContext value={{ sendInboundToastNotification }}>
      {children}
    </NotificationContext>
  )
}

export const useNotifications: () => INotificationContext = () => {
  const context = use(NotificationContext)
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider')
  }

  return context
}
